import { Flex, Form, InputNumber, Modal, Radio as AntdRadio, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { InfoBox } from "ui/InfoBox"
import { Radio } from "ui/Radio"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import styles from "./ExperienceDurationType.module.css"

export const DURATION_TYPES = {
  SINGLE_DAY: "single_day",
  MULTI_DAY: "multi_day",
}

export const DURATION_MODES = {
  FIXED: "fixed",
  DYNAMIC: "dynamic",
}

/**
 * Duration-mode radios with a guard on the switch to Dynamic.
 *
 * Dynamic reinterprets an experience's stored rates as per-day — a $1,000 whole-stay price becomes
 * $1,000 *per day* — so on an experience that already has booking options the change is confirmed
 * before it takes. Written as a wrapper because Form.Item supplies value/onChange to its child and
 * would override an onChange set directly on the radio group.
 */
const DurationModeRadios = ({ value, onChange, disabled, hasStoredPrices, t }) => {
  const handleChange = (event) => {
    const next = event.target.value
    const isSwitchingToDynamic = next === DURATION_MODES.DYNAMIC && value !== DURATION_MODES.DYNAMIC
    if (!isSwitchingToDynamic || !hasStoredPrices) {
      onChange(next)
      return
    }
    Modal.confirm({
      title: t("listings.tabs.scheduling.sections.duration_type.dynamic_switch_title"),
      content: t("listings.tabs.scheduling.sections.duration_type.dynamic_switch_body"),
      okText: t("listings.tabs.scheduling.sections.duration_type.dynamic_switch_confirm"),
      cancelText: t("global.cancel"),
      onOk: () => onChange(next)
    })
  }

  return (
    <AntdRadio.Group value={value} onChange={handleChange} disabled={disabled}>
      <Flex align="center" gap={spacings.XS}>
        <Radio value={DURATION_MODES.FIXED}>{t("listings.tabs.scheduling.sections.duration_type.fixed_duration")}</Radio>
        <Tooltip title={t("listings.tabs.scheduling.sections.duration_type.fixed_duration_tooltip")}>
          <InfoCircleOutlined style={{ color: "var(--color-text-secondary)", marginLeft: -8 }} />
        </Tooltip>
        <Radio value={DURATION_MODES.DYNAMIC}>{t("listings.tabs.scheduling.sections.duration_type.dynamic_dates")}</Radio>
        <Tooltip title={t("listings.tabs.scheduling.sections.duration_type.dynamic_dates_tooltip")}>
          <InfoCircleOutlined style={{ color: "var(--color-text-secondary)", marginLeft: -8 }} />
        </Tooltip>
      </Flex>
    </AntdRadio.Group>
  )
}

DurationModeRadios.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  hasStoredPrices: PropTypes.bool,
  t: PropTypes.func.isRequired
}

export const ExperienceDurationType = ({ disabled, form }) => {
  const { t } = useTranslation()
  const durationType = Form.useWatch("duration_type", form)
  const durationMode = Form.useWatch("duration_mode", form)
  const isMultiDay = durationType === DURATION_TYPES.MULTI_DAY
  const isDynamic = durationMode === DURATION_MODES.DYNAMIC
  // Only an experience that already has booking options can have its rates reinterpreted.
  const hasStoredPrices = (Form.useWatch("localPrices", form) || []).some((price) => !price?._destroy)

  return (
    <Card title={t("listings.tabs.scheduling.sections.duration_type.title")} className="mb-3xl">
      <Flex vertical gap={spacings.M}>
        <div>
          <Text size="m" fw={500} className="block mb-s">
            {t("listings.tabs.scheduling.sections.duration_type.experience_label")}
          </Text>
          <Form.Item name="duration_type" initialValue={DURATION_TYPES.SINGLE_DAY} rules={[{ required: true }]} className="mb-0">
            <AntdRadio.Group disabled={disabled}>
              <Radio value={DURATION_TYPES.SINGLE_DAY}>
                {t("listings.tabs.scheduling.sections.duration_type.single_day")}
              </Radio>
              <Radio value={DURATION_TYPES.MULTI_DAY}>
                {t("listings.tabs.scheduling.sections.duration_type.multi_day")}
              </Radio>
            </AntdRadio.Group>
          </Form.Item>
        </div>

        {isMultiDay && (
          <Flex vertical gap={spacings.S}>
            <Text size="m" fw={500} className="block">
              {t("listings.tabs.scheduling.sections.duration_type.duration_label")}
            </Text>
            <Form.Item
              name="duration_mode"
              initialValue={DURATION_MODES.FIXED}
              rules={[{ required: true }]}
              className="mb-0"
            >
              <DurationModeRadios disabled={disabled} hasStoredPrices={hasStoredPrices} t={t} />
            </Form.Item>

            {isDynamic ? (
              <Flex gap={spacings.M}>
                <div>
                  <Text size="m" className="block mb-xs">
                    {t("listings.tabs.scheduling.sections.duration_type.min_duration")}*
                  </Text>
                  <Form.Item
                    name="min_duration_days"
                    validateFirst
                    rules={[
                      { required: true, message: t("listings.tabs.scheduling.sections.duration_type.min_duration_required") },
                      { type: "number", min: 2, message: t("listings.tabs.scheduling.sections.duration_type.min_days_error") },
                    ]}
                    className="mb-0"
                  >
                    <InputNumber disabled={disabled} style={{ width: 174 }} />
                  </Form.Item>
                </div>
                <div>
                  <Text size="m" className="block mb-xs">
                    {t("listings.tabs.scheduling.sections.duration_type.max_duration")}*
                  </Text>
                  <Form.Item
                    name="max_duration_days"
                    dependencies={["min_duration_days"]}
                    validateFirst
                    rules={[
                      { required: true, message: t("listings.tabs.scheduling.sections.duration_type.max_duration_required") },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value) return Promise.resolve()
                          const minValue = getFieldValue("min_duration_days")
                          // Relative-to-min check takes priority: once min is set, "lower than min"
                          // is the meaningful error even when that also means below the 2-day floor.
                          if (minValue && value < minValue) {
                            return Promise.reject(new Error(t("listings.tabs.scheduling.sections.duration_type.max_greater_than_min")))
                          }
                          if (value < 2) {
                            return Promise.reject(new Error(t("listings.tabs.scheduling.sections.duration_type.min_days_error")))
                          }
                          return Promise.resolve()
                        },
                      }),
                    ]}
                    className="mb-0"
                  >
                    <InputNumber disabled={disabled} style={{ width: 174 }} />
                  </Form.Item>
                </div>
              </Flex>
            ) : (
              <InfoBox className={styles.fixedDurationHint}>
                {t("listings.tabs.scheduling.sections.duration_type.fixed_duration_hint")}
              </InfoBox>
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}

ExperienceDurationType.propTypes = {
  disabled: PropTypes.bool,
  form: PropTypes.object.isRequired,
}
